/**
 * Billing approvals (subscription engine, phase 4): credits, refunds and
 * extensions waiting for a second person. The person who asked can never
 * approve their own request; the server refuses it.
 */
import { api } from '@lipaflow/shared'

export interface PendingCredit {
  id: string
  tenant_id: string
  tenant_name: string | null
  amount: number
  source: string
  reason: string
  requested_by: string | null
  created_at: string | null
}

export interface PendingRefund {
  id: string
  tenant_id: string
  tenant_name: string | null
  amount: number
  reason: string
  requested_by: string
}

export interface PendingExtension {
  id: string
  tenant_id: string
  tenant_name: string | null
  kind: 'trial' | 'due_date' | 'grace' | 'service'
  days: number
  reason: string
  requested_by: string
}

export interface Approvals {
  credits: PendingCredit[]
  refunds: PendingRefund[]
  extensions: PendingExtension[]
}

export type ApprovalKind = 'credits' | 'refunds' | 'extensions'

export const billingApprovals = {
  /** The sector's billing changes waiting for a second approver. */
  async list(sector: string): Promise<Approvals> {
    return (await api.get<Approvals>('/admin/billing/approvals', { params: { vertical_code: sector } })).data
  },
  async decide(kind: ApprovalKind, id: string, approve: boolean): Promise<void> {
    await api.post(`/admin/billing/${kind}/${id}/decision`, { approve })
  },
}

// --- Making the changes (the customer panel) ------------------------------------

export interface CustomerCredit {
  id: string
  amount: number
  remaining: number
  source: 'goodwill' | 'manual' | string
  reason: string
  status: 'pending_approval' | 'available' | 'rejected' | 'void'
  created_at: string | null
}

export interface CustomerRefund {
  id: string
  credit_id: string
  amount: number
  reason: string
  status: 'pending_approval' | 'approved' | 'rejected' | 'paid'
  payment_reference: string | null
}

export interface UnpaidInvoice {
  id: string
  number: string
  amount: number
  paid_amount: number
  status: string
  due_date: string | null
}

export interface CustomerAdjustments {
  balance: number
  credits: CustomerCredit[]
  refunds: CustomerRefund[]
  unpaid_invoices: UnpaidInvoice[]
}

export type ExtensionKind = PendingExtension['kind']

/** What the server says after asking: applied now, or waiting for a second person. */
export interface Requested {
  id: string
  status: string
}

export const billingAdjustments = {
  async forCustomer(tenantId: string): Promise<CustomerAdjustments> {
    return (await api.get<CustomerAdjustments>(`/admin/tenants/${tenantId}/credits`)).data
  },
  /** Goodwill or manual credit. Above KES 5,000 it waits for a second person. */
  async grantCredit(tenantId: string, body: { amount: number; reason: string; source: 'goodwill' | 'manual' }): Promise<Requested> {
    return (await api.post<Requested>(`/admin/tenants/${tenantId}/credits`, body)).data
  },
  /** A cash refund of what's left of a credit. Always needs a second person. */
  async requestRefund(creditId: string, body: { amount: number; reason: string }): Promise<Requested> {
    return (await api.post<Requested>(`/admin/billing/credits/${creditId}/refunds`, body)).data
  },
  /** Record that an approved refund was paid out, with the payment's reference. */
  async markRefundPaid(refundId: string, reference: string): Promise<Requested> {
    return (await api.post<Requested>(`/admin/billing/refunds/${refundId}/paid`, { reference })).data
  },
  /** More trial, a later due date, more grace or free service days (D7 limits apply). */
  async requestExtension(
    tenantId: string,
    body: { kind: ExtensionKind; days: number; reason: string; invoice_id?: string },
  ): Promise<Requested> {
    return (await api.post<Requested>(`/admin/tenants/${tenantId}/extensions`, body)).data
  },
}
